import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ImportanceLevel } from "@/utils/weightedCompatibility";
import { cn } from "@/lib/utils";

interface CompactImportanceSelectorProps {
  value: ImportanceLevel;
  onChange: (value: ImportanceLevel) => void;
  label: string;
}

const importanceOptions: { level: ImportanceLevel; color: string; label: string }[] = [
  { level: "dont_care", color: "bg-slate-400", label: "Don't Care" },
  { level: "flexible", color: "bg-green-500", label: "Flexible" },
  { level: "somewhat_important", color: "bg-blue-500", label: "Nice to Have" },
  { level: "important", color: "bg-yellow-500", label: "Important" },
  { level: "very_important", color: "bg-orange-500", label: "Very Important" },
  { level: "deal_breaker", color: "bg-red-500", label: "Deal Breaker" },
];

export function CompactImportanceSelector({ value, onChange, label }: CompactImportanceSelectorProps) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Label className="text-xs text-muted-foreground min-w-[80px]">Importance:</Label>
      <div className="flex items-center gap-2">
        {importanceOptions.map((option) => (
          <TooltipProvider key={option.level} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onChange(option.level)}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all border-2",
                    option.color,
                    value === option.level
                      ? "ring-2 ring-offset-2 ring-foreground scale-110 border-background"
                      : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                  )}
                  aria-label={`Set ${label} importance to ${option.label}`}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm font-medium">{option.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
      <span className="text-xs font-medium text-foreground min-w-[100px]">
        {importanceOptions.find((o) => o.level === value)?.label}
      </span>
    </div>
  );
}
